import { useState, useEffect, Fragment, useRef } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { getDateOnlyFromISO } from '../../utils/dateHelpers';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { sanitizeHtml } from '../../utils/sanitize';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import {
  getTimeSpentValidationError,
  TIME_SPENT_INCREMENT_MINUTES,
} from '../../utils/timeEntryTimeSpent';

interface TypeOption {
  value: string;
  label: string;
}

interface GroupOption {
  id: string;
  name: string;
}

interface CustomerOption {
  id: string;
  accountName: string;
}

interface EngagementOption {
  id: string;
  name: string;
  number?: string;
}

interface FieldErrors {
  customerId?: string;
  engagementId?: string;
  type?: string;
  groupId?: string;
  timeSpent?: string;
  workedDate?: string;
  description?: string;
}

export default function TimeEntryEdit() {
  const { id } = useParams<{ id: string }>();
  const { formatDateTime, getToday } = useDateFormatter();
  const navigate = useNavigate();
  const location = useLocation();
  const timeEntriesListSearch = (location.state as { timeEntriesListSearch?: string } | null)?.timeEntriesListSearch;
  const timeEntriesListPath =
    timeEntriesListSearch !== undefined ? `/time-entries${timeEntriesListSearch}` : '/time-entries';
  const navigateBackToTimeEntries = () => navigate(timeEntriesListPath);

  // Get today's date in user's preferred timezone
  const todayInUserTimezone = getToday();
  const queryClient = useQueryClient();
  const { canUpdateEntity, loading: permissionsLoading } = useUserPermissions();
  const { hasAnyRole, user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Entry status and rejection info
  const [entryStatus, setEntryStatus] = useState<'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
  const [rejectionInfo, setRejectionInfo] = useState<{
    rejectedBy: string;
    remarks: string;
    actionRequired: string;
    rejectedAt: string;
  } | null>(null);

  const [typeOptions, setTypeOptions] = useState<TypeOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [engagements, setEngagements] = useState<EngagementOption[]>([]);

  const [formData, setFormData] = useState({
    customerId: '',
    engagementId: '',
    type: '',
    groupId: '',
    task: '',
    timeSpent: '',
    workedDate: '',
    description: ''
  });

  // Store display names for readonly fields (non-admin/manager users)
  const [customerName, setCustomerName] = useState('');
  const [engagementName, setEngagementName] = useState('');
  const [entryUserName, setEntryUserName] = useState('');

  const [typeQuery, setTypeQuery] = useState('');
  const [groupQuery, setGroupQuery] = useState('');
  const prevCustomerIdRef = useRef<string | null>(null);
  const prevEngagementIdRef = useRef<string | null>(null);
  const entryUserIdRef = useRef<string>('');
  const entryGroupRef = useRef<GroupOption | null>(null);

  const [entryOwnerId, setEntryOwnerId] = useState<string | null>(null);

  const canEdit = canUpdateEntity('time_entries');
  const isAdminRole = hasAnyRole(['admin', 'super_admin']);
  const isAdminOrManager = hasAnyRole(['admin', 'manager', 'super_admin']);
  const canEditThisEntry = canEdit && (isAdminRole || !entryOwnerId || entryOwnerId === user?.id);
  const canEditCustomerEngagement =
    isAdminOrManager && (entryStatus !== 'APPROVED' || hasAnyRole(['admin', 'super_admin']));

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!isAdminOrManager || !canEditCustomerEngagement) return;
    if (formData.customerId) {
      fetchEngagements(formData.customerId);
    } else {
      setEngagements([]);
    }
    if (prevCustomerIdRef.current !== null && prevCustomerIdRef.current !== formData.customerId) {
      setFormData((prev) => ({ ...prev, engagementId: '', groupId: '', type: '' }));
    }
    prevCustomerIdRef.current = formData.customerId;
  }, [formData.customerId, isAdminOrManager, canEditCustomerEngagement]);

  useEffect(() => {
    if (!formData.engagementId) {
      if (isAdminOrManager && canEditCustomerEngagement) {
        setGroupOptions([]);
        setTypeOptions([]);
      }
      return;
    }
    if (isAdminOrManager && canEditCustomerEngagement) {
      fetchUserGroups(formData.engagementId);
      fetchTypeOptions(formData.engagementId);
    }
    if (prevEngagementIdRef.current !== null && prevEngagementIdRef.current !== formData.engagementId) {
      entryGroupRef.current = null;
      setFormData((prev) => ({ ...prev, groupId: '', type: '' }));
    }
    prevEngagementIdRef.current = formData.engagementId;
  }, [formData.engagementId, isAdminOrManager, canEditCustomerEngagement]);

  // Adjust worked date if it exceeds max date after timezone change
  useEffect(() => {
    if (todayInUserTimezone && formData.workedDate && formData.workedDate > todayInUserTimezone) {
      setFormData(prev => ({ ...prev, workedDate: todayInUserTimezone }));
    }
  }, [todayInUserTimezone]);

  const mergeWithEntryGroup = (groups: GroupOption[]): GroupOption[] => {
    const fallback = entryGroupRef.current;
    if (!fallback?.id || groups.some((g) => g.id === fallback.id)) {
      return groups;
    }
    return [fallback, ...groups];
  };

  const buildUserGroupsUrl = (engagementId: string) => {
    const params = new URLSearchParams({ engagementId });
    const entryUserId = entryUserIdRef.current;
    if (isAdminOrManager && entryUserId && entryUserId !== user?.id) {
      params.append('userId', entryUserId);
    }
    return `/time-entries/user-groups?${params.toString()}`;
  };

  const fetchInitialData = async () => {
    try {
      const entryRes = await api.get(`/time-entries/${id}`);
      const entry = entryRes.data;
      const entryCustomerId = entry.customerId || entry.Customer?.id || '';
      const entryEngagementId = entry.engagementId || entry.Engagement?.id || '';

      if (hasAnyRole(['admin', 'manager', 'super_admin'])) {
        const customersRes = await api.get('/customers?limit=1000');
        setCustomers(customersRes.data.customers || customersRes.data || []);
      }

      const typesRes = await api.get(
        entryEngagementId
          ? `/time-entries/types?engagementId=${entryEngagementId}`
          : '/time-entries/types'
      );
      setTypeOptions(typesRes.data || []);

      const entryGroupId = entry.groupId || entry.Group?.id || '';
      entryUserIdRef.current = entry.userId || entry.User?.id || '';
      setEntryOwnerId(entryUserIdRef.current || null);
      if (entry.Group?.id && entry.Group?.name) {
        entryGroupRef.current = { id: entry.Group.id, name: entry.Group.name };
      } else if (entryGroupId) {
        entryGroupRef.current = { id: entryGroupId, name: entry.Group?.name || 'Selected group' };
      }

      setFormData({
        customerId: entryCustomerId,
        engagementId: entryEngagementId,
        type: entry.type || '',
        groupId: entryGroupId,
        task: entry.task || '',
        timeSpent: entry.timeSpent?.toString() || '',
        workedDate: getDateOnlyFromISO(entry.workedDate),
        description: entry.description || ''
      });

      // Store display names for readonly fields
      setCustomerName(entry.Customer?.accountName || '');
      setEngagementName(entry.Engagement?.name || '');
      setEntryUserName(`${entry.User?.firstName || ''} ${entry.User?.lastName || ''}`.trim());

      // Set entry status and rejection info
      setEntryStatus(entry.status || 'PENDING');
      if (entry.status === 'REJECTED' && entry.RejectedBy) {
        setRejectionInfo({
          rejectedBy: `${entry.RejectedBy.firstName || ''} ${entry.RejectedBy.lastName || ''}`.trim(),
          remarks: entry.remarks || '',
          actionRequired: entry.actionRequired || '',
          rejectedAt: entry.rejectedAt || '',
        });
      }

      // Fetch billing groups for this engagement (entry owner's groups when admin/manager)
      if (entryEngagementId) {
        try {
          const groupsRes = await api.get(buildUserGroupsUrl(entryEngagementId));
          setGroupOptions(mergeWithEntryGroup(groupsRes.data || []));
        } catch (err) {
          console.error('Error fetching groups:', err);
          if (entryGroupRef.current) {
            setGroupOptions([entryGroupRef.current]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load time entry');
      navigateBackToTimeEntries();
    } finally {
      setLoading(false);
    }
  };

  const fetchEngagements = async (customerId: string) => {
    try {
      const response = await api.get(`/engagements?customerId=${customerId}&limit=1000`);
      setEngagements(response.data.engagements || response.data || []);
    } catch (error) {
      console.error('Error fetching engagements:', error);
    }
  };

  const fetchUserGroups = async (engagementId: string) => {
    try {
      const response = await api.get(buildUserGroupsUrl(engagementId));
      setGroupOptions(mergeWithEntryGroup(response.data || []));
    } catch (error) {
      console.error('Error fetching groups:', error);
      if (entryGroupRef.current) {
        setGroupOptions([entryGroupRef.current]);
      }
    }
  };

  const fetchTypeOptions = async (engagementId: string) => {
    try {
      const response = await api.get(`/time-entries/types?engagementId=${engagementId}`);
      setTypeOptions(response.data || []);
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field as keyof FieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const filteredTypes = typeQuery === ''
    ? typeOptions
    : typeOptions.filter((option) =>
        option.label.toLowerCase().includes(typeQuery.toLowerCase())
      );

  const filteredGroups = groupQuery === ''
    ? groupOptions
    : groupOptions.filter((option) =>
        option.name.toLowerCase().includes(groupQuery.toLowerCase())
      );

  const selectedType = typeOptions.find(t => t.value === formData.type);
  const selectedGroup =
    groupOptions.find((g) => g.id === formData.groupId) ||
    (formData.groupId && entryGroupRef.current?.id === formData.groupId
      ? entryGroupRef.current
      : undefined);

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    if (canEditCustomerEngagement) {
      if (!formData.customerId) {
        errors.customerId = 'Customer is required';
      }
      if (!formData.engagementId) {
        errors.engagementId = 'Engagement is required';
      }
    }

    if (!formData.type) {
      errors.type = 'Type is required';
    }
    const timeSpentError = getTimeSpentValidationError(formData.timeSpent);
    if (timeSpentError) {
      errors.timeSpent = timeSpentError;
    }
    if (!formData.workedDate) {
      errors.workedDate = 'Worked Date is required';
    } else {
      const workedDate = new Date(formData.workedDate);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (workedDate > today) {
        errors.workedDate = 'Worked Date cannot be in the future';
      }
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const updateData: Record<string, unknown> = {
        type: formData.type,
        groupId: formData.groupId || undefined,
        task: formData.task || undefined,
        timeSpent: parseInt(formData.timeSpent),
        workedDate: formData.workedDate,
        description: formData.description
      };

      if (canEditCustomerEngagement) {
        updateData.customerId = formData.customerId;
        updateData.engagementId = formData.engagementId;
      }

      // If entry was rejected, use the resubmit endpoint to reset status to PENDING
      if (entryStatus === 'REJECTED') {
        await api.post(`/time-entries/${id}/resubmit`, updateData);
        toast.success('Time entry resubmitted for approval');
      } else {
        await api.put(`/time-entries/${id}`, updateData);
        toast.success('Time entry updated successfully');
      }

      // Invalidate all time entry queries to refresh the list (including hierarchy view)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['timeEntries'] }),
        queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] }),
        queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] }),
      ]);
      navigate(timeEntriesListPath);
    } catch (error: any) {
      console.error('Error updating time entry:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update time entry';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (permissionsLoading || loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!canEditThisEntry) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don&apos;t have permission to edit this time entry.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link
          to={timeEntriesListPath}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Time Entries
        </Link>

        <div className="flex justify-between items-start">
          <h1 className="text-2xl font-bold text-gray-900">Edit Time Entry</h1>

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || (entryStatus === 'APPROVED' && !hasAnyRole(['admin', 'super_admin']))}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (entryStatus === 'REJECTED' ? 'Resubmit' : 'Save')}
            </button>
            <Link
              to={timeEntriesListPath}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>

      {/* Rejection Alert Banner */}
      {entryStatus === 'REJECTED' && rejectionInfo && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-800">Time Entry Rejected</h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="mb-2">
                  <span className="font-medium">Rejected by:</span> {rejectionInfo.rejectedBy}
                  {rejectionInfo.rejectedAt && (
                    <span className="ml-2 text-red-600">
                      on {formatDateTime(rejectionInfo.rejectedAt)}
                    </span>
                  )}
                </p>
                {rejectionInfo.remarks && (
                  <div className="mb-2">
                    <span className="font-medium">Rejection Reason:</span>
                    <div
                      className="mt-1 p-2 bg-red-100 rounded text-red-800"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(rejectionInfo.remarks) }}
                    />
                  </div>
                )}
                {rejectionInfo.actionRequired && (
                  <div>
                    <span className="font-medium">Action Required:</span>
                    <div
                      className="mt-1 p-2 bg-red-100 rounded text-red-800"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(rejectionInfo.actionRequired) }}
                    />
                  </div>
                )}
              </div>
              <p className="mt-3 text-sm text-red-600 font-medium">
                Please make the required changes and click "Resubmit" to send for approval again.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Approved Entry Warning */}
      {entryStatus === 'APPROVED' && !hasAnyRole(['admin', 'super_admin']) && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">This time entry has been approved</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Approved entries cannot be modified. Contact an administrator if changes are needed.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Row 1: User and Customer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">User</label>
              <div className="col-span-2">
                <input
                  type="text"
                  value={entryUserName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">Customer</label>
              <div className="col-span-2">
                {canEditCustomerEngagement ? (
                  <>
                    <select
                      value={formData.customerId}
                      onChange={(e) => handleInputChange('customerId', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        fieldErrors.customerId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Customer</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.accountName}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.customerId && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.customerId}</p>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    value={customerName}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Engagement and Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">Engagement</label>
              <div className="col-span-2">
                {canEditCustomerEngagement ? (
                  <>
                    <select
                      value={formData.engagementId}
                      onChange={(e) => handleInputChange('engagementId', e.target.value)}
                      disabled={!formData.customerId}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                        fieldErrors.engagementId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Engagement</option>
                      {engagements.map((engagement) => (
                        <option key={engagement.id} value={engagement.id}>
                          {engagement.name}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.engagementId && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.engagementId}</p>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    value={engagementName}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">
                Type <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <Combobox
                  value={formData.type}
                  onChange={(value) => handleInputChange('type', value)}
                >
                  <div className="relative">
                    <div className="relative w-full">
                      <Combobox.Input
                        className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                          fieldErrors.type ? 'border-red-500' : 'border-gray-300'
                        }`}
                        displayValue={() => selectedType?.label || ''}
                        onChange={(event) => setTypeQuery(event.target.value)}
                        placeholder="Select or search type..."
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </Combobox.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                      afterLeave={() => setTypeQuery('')}
                    >
                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredTypes.length === 0 && typeQuery !== '' ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            Nothing found.
                          </div>
                        ) : (
                          filteredTypes.map((option) => (
                            <Combobox.Option
                              key={option.value}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                  active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                }`
                              }
                              value={option.value}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                    {option.label}
                                  </span>
                                  {selected ? (
                                    <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'}`}>
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </Transition>
                  </div>
                </Combobox>
                {fieldErrors.type && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.type}</p>
                )}
              </div>
            </div>
          </div>

          {/* Row 3: Time Spent and Worked Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">
                Time Spent (In Mins) <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <input
                  type="number"
                  min={TIME_SPENT_INCREMENT_MINUTES}
                  step={TIME_SPENT_INCREMENT_MINUTES}
                  value={formData.timeSpent}
                  onChange={(e) => handleInputChange('timeSpent', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    fieldErrors.timeSpent ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g. 15, 30, 45, 60"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter time in 15-minute increments only
                </p>
                {fieldErrors.timeSpent && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.timeSpent}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">
                Worked Date <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <DateTimePickerInput
                  value={formData.workedDate}
                  onChange={(value) => handleInputChange('workedDate', value)}
                  max={todayInUserTimezone}
                  showTimeSelect={false}
                  error={!!fieldErrors.workedDate}
                  placeholder="Select date"
                />
                {fieldErrors.workedDate && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.workedDate}</p>
                )}
              </div>
            </div>
          </div>

          {/* Row 4: Group and Task */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">Group</label>
              <div className="col-span-2">
                <Combobox
                  value={formData.groupId}
                  onChange={(value) => handleInputChange('groupId', value)}
                >
                  <div className="relative">
                    <div className="relative w-full">
                      <Combobox.Input
                        className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                          fieldErrors.groupId ? 'border-red-500' : 'border-gray-300'
                        }`}
                        displayValue={() => selectedGroup?.name || ''}
                        onChange={(event) => setGroupQuery(event.target.value)}
                        placeholder="Select or search group..."
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </Combobox.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                      afterLeave={() => setGroupQuery('')}
                    >
                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredGroups.length === 0 && groupQuery !== '' ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            Nothing found.
                          </div>
                        ) : filteredGroups.length === 0 ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No billing groups available for this engagement.
                          </div>
                        ) : (
                          filteredGroups.map((option) => (
                            <Combobox.Option
                              key={option.id}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                  active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                }`
                              }
                              value={option.id}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                    {option.name}
                                  </span>
                                  {selected ? (
                                    <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'}`}>
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </Transition>
                  </div>
                </Combobox>
                {fieldErrors.groupId && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.groupId}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">Task</label>
              <div className="col-span-2">
                <input
                  type="text"
                  value={formData.task}
                  onChange={(e) => handleInputChange('task', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter task/ticket reference (optional)"
                />
              </div>
            </div>
          </div>

          {/* Row 5: Description (full width) */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-start">
            <label className="text-sm font-medium text-gray-700 md:col-span-1 pt-2">
              Description <span className="text-red-500">*</span>
            </label>
            <div className="md:col-span-5">
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  fieldErrors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter description of work performed"
              />
              {fieldErrors.description && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.description}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
